import { Grow, Popper } from '@material-ui/core'
import PropTypes from 'prop-types'
import React, { useEffect, useMemo, useRef } from 'react'
import SydButton from '../../../commonDesign/Button'
import FilterTypeSelector from './FilterTypeSelector'
import { useFilterSelectContext } from './context'

export const FilterMenu = ({
  anchorRef,
  filterTypeSelectProps,
  resetButton = { enable: true, text: 'Reset', className: 'flex-1' },
  confirmButton = { enable: true, text: 'Submit', className: 'flex-1' },
  children
}) => {
  const {
    showDetails,
    setShowDetails,
    setSelectedValues,
    selectedValues,
    setVariableValue,
    hasMultipleOptions,
    clearPersistence
  } = useFilterSelectContext()
  const selectorRef = useRef(null)
  const popperRef = useRef(null)

  const placement = useMemo(() => {
    const rect = anchorRef.current?.getBoundingClientRect()
    if (!rect) return 'bottom-start'
    const spaceLeft = rect.left
    const spaceRight = window.innerWidth - rect.right
    return spaceLeft > spaceRight ? 'bottom-end' : 'bottom-start'
  }, [anchorRef])

  useEffect(() => {
    if (!showDetails) return

    const handleClickOutside = (event) => {
      // Check if click is within the popper content
      if (popperRef.current && popperRef.current.contains(event.target)) {
        return
      }
      if (anchorRef.current && anchorRef.current.contains(event.target)) {
        return
      }
      if (
        event.target.closest('[role="menu"]') ||
        event.target.closest('[role="menuitem"]') ||
        ['listbox', 'option'].includes(event.target.parentElement.role) ||
        event.target.ariaHidden === 'true'
      ) {
        return
      }

      setShowDetails(false)
    }

    document.addEventListener('mousedown', handleClickOutside)
    return () => {
      document.removeEventListener('mousedown', handleClickOutside)
    }
  }, [showDetails, anchorRef, setShowDetails])

  const {
    enable: resetButtonEnabled,
    text: resetButtonText,
    className: resetButtonClassName, ...resetButtonProps
  } = resetButton
  const {
    enable: confirmButtonEnabled,
    text: confirmButtonText,
    className: confirmButtonClassName, ...confirmButtonProps
  } = confirmButton

  return (
    <Popper
      open={showDetails}
      anchorEl={anchorRef.current}
      role={undefined}
      transition
      placement={placement}
      style={{
        zIndex: 10
      }}
    >
      {({ TransitionProps, placement }) => (
        <Grow
          {...TransitionProps}
          style={{
            transformOrigin:
              placement === 'bottom' ? 'center top' : 'center bottom'
          }}
        >
          <div
            ref={popperRef}
            className='mt-2 radius bg-surface-400 p-2 shadow-lg relative'
            style={{ minWidth: '400px' }}
          >
            <div className='flex flex-col gap-md'>
              {hasMultipleOptions && (
                <div ref={selectorRef}>
                  <FilterTypeSelector {...filterTypeSelectProps} />
                </div>
              )}
              {children}

              {(resetButtonEnabled || confirmButtonEnabled) && (
                <div className='flex gap-2'>
                  {resetButtonEnabled && (
                    <SydButton
                      disabled={!selectedValues?.length}
                      variant='secondary'
                      onClick={() => {
                        setSelectedValues([])
                        setVariableValue([])
                        setShowDetails(false)
                        clearPersistence()
                      }}
                      size='sm'
                      className={resetButtonClassName}
                      {...resetButtonProps}
                    >
                      {resetButtonText}
                    </SydButton>
                  )}
                  {confirmButtonEnabled && (
                    <SydButton
                      variant='primary'
                      onClick={() => {
                        setShowDetails(false)
                      }}
                      size='sm'
                      className={confirmButtonClassName}
                      {...confirmButtonProps}
                    >
                      {confirmButtonText}
                    </SydButton>
                  )}
                </div>
              )}
            </div>
          </div>
        </Grow>
      )}
    </Popper>
  )
}

FilterMenu.propTypes = {
  anchorRef: PropTypes.shape({
    current: PropTypes.any
  }),
  children: PropTypes.node,
  filterTypeSelectProps: PropTypes.object,
  resetButton: PropTypes.shape({
    enable: PropTypes.bool,
    text: PropTypes.object
  }),
  confirmButton: PropTypes.shape({
    enable: PropTypes.bool,
    text: PropTypes.object
  })
}
